import { DatePipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule, ValidatorFn } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import { FasciaOraria } from '../../calendar/calendar.utils';
import { HolidaysService } from '../../calendar/services/holidays.service';
import { DatepickerFieldComponent } from '../datepicker-field/datepicker-field.component';
import {
  buildHolidayFilter,
  combineDateAndTime,
  computeMinTime,
  fineEntroChiusuraValidator,
  latestHHmm,
  ultimoInizioHHmm,
} from '../event-date-time-form.utils';
import { TimepickerFieldComponent } from '../timepicker-field/timepicker-field.component';

/**
 * Date + time fields for an appuntamento (used both to insert a new
 * appuntamento and to reschedule an existing one), together with the rules
 * that constrain them: minimum selectable date/time and holiday filtering.
 * The consumer only owns the FormGroup (built via `buildEventDateTimeForm`);
 * this component owns everything about how the fields behave.
 */
@UntilDestroy()
@Component({
  selector: 'siat-event-date-time-fields',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DatepickerFieldComponent,
    TimepickerFieldComponent,
    TranslatePipe,
  ],
  templateUrl: './event-date-time-fields.component.html',
})
export class EventDateTimeFieldsComponent {
  /*
   * The form carrying dateStart/time controls.
   */
  readonly formGroup = input.required<FormGroup>();

  /*
   * Minimum selectable date, defaults to today.
   */
  readonly minDate = input<Date>(new Date());

  /*
   * Label of the date field.
   */
  readonly dateLabel = input<string>('Data');

  /*
   * Label of the time field, defaults to the translated "Ora".
   */
  readonly timeLabel = input<string | null>(null);

  /*
   * Duration kept by the event: when set, the resulting end is shown read-only.
   */
  readonly durataMs = input<number | null>(null);

  /*
   * Shows the optional end date/time fields (the form must carry dateEnd/timeEnd).
   */
  readonly withEnd = input<boolean>(false);

  /*
   * Opening window of the structure, limiting the selectable times.
   */
  readonly fasciaOraria = input<Pick<FasciaOraria, 'inizio' | 'fine'> | null>(
    null,
  );

  /*
   * Duration of the appointment, so the last selectable start still ends by closing.
   */
  readonly durataAppuntamentoMs = input<number | null>(null);

  /*
   * End computed from the selected start and `durataMs`.
   */
  readonly fineCalcolata = signal<Date | null>(null);

  /*
   * Hint of the time field with the computed end, with its date when it falls on another day.
   */
  readonly fineHint = computed<string | null>(() => {
    const fine = this.fineCalcolata();
    if (!fine || this.withEnd()) return null;
    const { dateStart } = this.formGroup().value;
    const stessoGiorno =
      dateStart && new Date(dateStart).toDateString() === fine.toDateString();
    const formato = stessoGiorno ? 'HH:mm' : 'dd/MM/yyyy HH:mm';
    return `Orario fine: ${this.datePipe.transform(fine, formato)}`;
  });

  /*
   * Formats the computed end.
   */
  private readonly datePipe = new DatePipe('it-IT');

  /*
   * The service for holidays.
   */
  private readonly holidaysService = inject(HolidaysService);

  /**
   * Holiday filter for the datepicker, shared by all date/time fields.
   */
  readonly holidayFilter = buildHolidayFilter(this.holidaysService);

  /*
   * Minimum selectable time, computed from dateStart.
   */
  readonly minTime = signal<string>('');

  /*
   * Minimum selectable start time: the later of the opening time and the current minimum.
   */
  readonly minTimeInizio = computed(() =>
    latestHHmm(this.minTime(), this.fasciaOraria()?.inizio),
  );

  /*
   * Minimum selectable end time: the opening time, if any.
   */
  readonly minTimeFine = computed(() => this.fasciaOraria()?.inizio ?? null);

  /*
   * Maximum selectable time: the closing time, if any.
   */
  readonly maxTimeFascia = computed(() => this.fasciaOraria()?.fine ?? null);

  /*
   * Maximum selectable start time: one step before closing, so the closing time can't be a start.
   */
  readonly maxTimeInizio = computed(() => {
    const fine = this.fasciaOraria()?.fine;
    return fine ? ultimoInizioHHmm(fine, this.durataAppuntamentoMs()) : null;
  });

  /*
   * Validator on the computed end currently attached to the form.
   */
  private fineChiusuraValidator: ValidatorFn | null = null;

  constructor() {
    effect(() => {
      const dateStartControl = this.formGroup().get('dateStart');
      this.minTime.set(computeMinTime(dateStartControl?.value));
      dateStartControl?.valueChanges
        .pipe(untilDestroyed(this))
        .subscribe((value) => this.minTime.set(computeMinTime(value)));
    });
    effect(() => {
      const formGroup = this.formGroup();
      const durataMs = this.durataMs();
      if (!this.withEnd()) return;
      formGroup.valueChanges
        .pipe(untilDestroyed(this))
        .subscribe(() => this.seguiInizio(formGroup, durataMs));
    });
    effect(() => {
      const formGroup = this.formGroup();
      const durataMs = this.durataMs();
      this.fineCalcolata.set(this.computeFine(formGroup, durataMs));
      formGroup.valueChanges
        .pipe(untilDestroyed(this))
        .subscribe(() =>
          this.fineCalcolata.set(this.computeFine(formGroup, durataMs)),
        );
    });
    effect(() => {
      const formGroup = this.formGroup();
      const fascia = this.fasciaOraria();
      const durataMs = this.durataMs();
      const withEnd = this.withEnd();
      this.applyFineChiusuraValidator(formGroup, fascia, durataMs, withEnd);
    });
  }

  /**
   * Keeps the computed-end-within-closing validator on the form in sync with the window and duration.
   */
  private applyFineChiusuraValidator(
    formGroup: FormGroup,
    fascia: Pick<FasciaOraria, 'inizio' | 'fine'> | null,
    durataMs: number | null,
    withEnd: boolean,
  ): void {
    if (this.fineChiusuraValidator) {
      formGroup.removeValidators(this.fineChiusuraValidator);
    }
    this.fineChiusuraValidator =
      fascia && durataMs && !withEnd
        ? fineEntroChiusuraValidator(fascia.fine, () => durataMs)
        : null;
    if (this.fineChiusuraValidator) {
      formGroup.addValidators(this.fineChiusuraValidator);
    }
    formGroup.updateValueAndValidity();
  }

  /**
   * Moves the end along with the start, keeping the duration, until the user edits the end.
   */
  private seguiInizio(formGroup: FormGroup, durataMs: number | null): void {
    const dateEnd = formGroup.get('dateEnd');
    const timeEnd = formGroup.get('timeEnd');
    if (!dateEnd || !timeEnd || dateEnd.dirty || timeEnd.dirty) return;
    const fine = this.computeFine(formGroup, durataMs);
    if (!fine) return;
    dateEnd.setValue(fine, { emitEvent: false });
    timeEnd.setValue(fine, { emitEvent: false });
  }

  /**
   * End of the event for the selected start, keeping the given duration.
   * @returns {Date | null} The end, null when the start or the duration is missing
   */
  private computeFine(
    formGroup: FormGroup,
    durataMs: number | null,
  ): Date | null {
    const { dateStart, time } = formGroup.value;
    if (!durataMs || durataMs < 0 || !dateStart || !time) return null;
    return new Date(combineDateAndTime(dateStart, time).getTime() + durataMs);
  }
}
